import Component from '@glimmer/component'
import type RexDashboardIndexRoute from 'teacher-ui-client/reporting/ui-scope/rex/dashboard/index/route'
import type RouterService from '@ember/routing/router-service'
import { service } from '@ember/service'
import { isPresent } from '@ember/utils'
import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { ReportingDashboardControls } from 'teacher-ui-client/components/reporting/dashboard-controls'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import { array } from '@ember/helper'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import RouteTemplate from 'ember-route-template'
import { camelize } from '@ember/string'
import { themeKeys as statBoxThemes } from 'teacher-ui-client/components/giraffe/stat-box/themes'
import { t } from 'ember-intl'
import { not } from 'ember-truth-helpers'
import { LinkTo } from '@ember/routing'
import { DashboardStadiumEvents } from 'teacher-ui-client/components/giraffe/charts/dashboard/stadium-events'
import { DashboardBooksReadByLevel } from 'teacher-ui-client/components/giraffe/charts/dashboard/books-read/by-level'
import { DashboardBooksReadByGenre } from 'teacher-ui-client/components/giraffe/charts/dashboard/books-read/by-genre'
import { DashboardCourseProgress } from 'teacher-ui-client/components/giraffe/charts/dashboard/course-progress'
import { DashboardAssessmentScores } from 'teacher-ui-client/components/giraffe/charts/dashboard/assessment-scores'
import { DashboardLexileGrowth } from 'teacher-ui-client/components/giraffe/charts/dashboard/lexile-growth'
import { DashboardTotalUsageOverTime } from 'teacher-ui-client/components/giraffe/charts/dashboard/total-usage-over-time'
import { ReportingDashboardDynamic } from 'teacher-ui-client/components/tiles/reporting-dashboard/dynamic'
import { ReportingDashboardStatic } from 'teacher-ui-client/components/tiles/reporting-dashboard/static'
import { AwardsEarnedTrophies } from 'teacher-ui-client/components/charts/dashboard/awards-earned/trophies'

interface Signature {
  Args: { model: ModelFor<RexDashboardIndexRoute> }
}

export class RexDashboardIndexPage extends Component<Signature> {
  @service router!: RouterService

  readonly routePrefix = 'reporting.ui-scope.rex.dashboard'

  get totalUsageBoxes() {
    return [
      {
        category: 'lessons',
        theme: statBoxThemes.THEME_OCEANY_BLUE,
      },
      {
        category: 'quizzes',
        theme: statBoxThemes.THEME_WL_BLUE,
      },
      {
        category: 'assessments',
        theme: statBoxThemes.THEME_JUICY_ORANGE,
      },
      {
        category: 'books',
        theme: statBoxThemes.THEME_PURPLY_PINK,
      },
      {
        category: 'spelling',
        theme: statBoxThemes.THEME_GRAPEY_GREEN,
      },
    ]
  }

  get isSummaryDataPresent() {
    return isPresent(this.args.model.totalUsageOverTime)
  }

  goToCategory = (category: string) => {
    switch (category) {
      case 'lessons':
        return void this.router.transitionTo('reporting.ui-scope.rex.course-progress.my-lessons')
      case 'quizzes':
        return void this.router.transitionTo('reporting.ui-scope.rex.quiz-scores')
      case 'assessments':
        return void this.router.transitionTo('reporting.ui-scope.rex.assessment-scores')
      case 'books':
        return void this.router.transitionTo('reporting.ui-scope.rex.books-read.by-genre')
      case 'spelling':
        return void this.router.transitionTo('reporting.ui-scope.rex.course-progress.spelling')
      default:
        return
    }
  }

  <template>
    <div class="mx-4">
      <ReportingTeacherUiHeader @title={{t "reporting.dashboardTitles.rex"}} />
      <span class="float-right print:hidden">
        <PhosphorIcon @icon="phosphor-info-fill" class="text-dusty-black-500 h-3 w-3 cursor-pointer" />
        <Tooltip @text={{t "reporting.dashboardTooltip"}} />
      </span>
      <ReportingDashboardControls
        @students={{@model.scopedStudents}}
        @routePrefix={{this.routePrefix}}
        @exportOptions={{array ExportType.print}}
        @isDataPresent={{this.isSummaryDataPresent}}
        @studentRouteName="reporting.ui-scope.rex.student-summary"
      />

      <ReportingDashboardDynamic
        data-test-total-usage-row
        class="page-break-after-always mb-4"
        @empty={{not this.isSummaryDataPresent}}
        @title={{t "reporting.chartTitles.dashboard.totalUsageOverTime"}}
        @tooltip={{t "reporting.tooltips.dashboard.totalUsageOverTime"}}
      >
        {{#if @model.totalUsageOverTime}}
          <DashboardTotalUsageOverTime
            @product={{@model.product}}
            @boxes={{this.totalUsageBoxes}}
            @data={{@model.totalUsageOverTime}}
            @tileClickAction={{this.goToCategory}}
            @uiScope={{camelize @model.uiScope.scope}}
          />
        {{/if}}
      </ReportingDashboardDynamic>

      <div class="grid auto-rows-[26rem] grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 print:grid-cols-3">
        <LinkTo data-test-class-progress-comprehension @route="reporting.ui-scope.rex.course-progress.my-lessons">
          <ReportingDashboardStatic
            class="h-full"
            @empty={{not @model.courseProgressByGrade}}
            @title={{t "reporting.chartTitles.dashboard.courseProgressByGrade.rex"}}
            @tooltip={{t "reporting.tooltips.dashboard.courseProgressByGrade.rex"}}
          >
            {{#if @model.courseProgressByGrade}}
              <DashboardCourseProgress
                @data={{@model.courseProgressByGrade}}
                @product={{@model.product}}
                @precinct="my_lessons"
              />
            {{/if}}
          </ReportingDashboardStatic>
        </LinkTo>

        <LinkTo data-test-lexile-details @route="reporting.ui-scope.rex.lexile-details">
          <ReportingDashboardStatic
            class="h-full"
            @empty={{not @model.lexileGrowth}}
            @title={{t "reporting.chartTitles.dashboard.lexileGrowth"}}
            @tooltip={{t "reporting.tooltips.dashboard.lexileGrowth"}}
          >
            {{#if @model.lexileGrowth}}
              <DashboardLexileGrowth @data={{@model.lexileGrowth}} />
            {{/if}}
          </ReportingDashboardStatic>
        </LinkTo>

        <LinkTo data-test-assessments-completed @route="reporting.ui-scope.rex.assessment-scores">
          <ReportingDashboardStatic
            class="h-full"
            @empty={{not @model.assessmentScores}}
            @title={{t "reporting.chartTitles.dashboard.assessmentScores.rex"}}
            @tooltip={{t "reporting.tooltips.dashboard.assessmentScores.rex"}}
          >
            {{#if @model.assessmentScores}}
              <DashboardAssessmentScores @data={{@model.assessmentScores}} />
            {{/if}}
          </ReportingDashboardStatic>
        </LinkTo>

        <LinkTo data-test-class-progress-spelling @route="reporting.ui-scope.rex.course-progress.spelling">
          <ReportingDashboardStatic
            class="h-full"
            @empty={{not @model.spellingCourseProgress}}
            @title={{t "reporting.chartTitles.dashboard.spellingCourseProgress"}}
            @tooltip={{t "reporting.tooltips.dashboard.spellingCourseProgress"}}
          >
            {{#if @model.spellingCourseProgress}}
              <DashboardCourseProgress
                @data={{@model.spellingCourseProgress}}
                @product={{@model.product}}
                @precinct="spelling"
              />
            {{/if}}
          </ReportingDashboardStatic>
        </LinkTo>

        <LinkTo data-test-books-read-by-genre @route="reporting.ui-scope.rex.books-read.by-genre">
          <ReportingDashboardStatic
            class="h-full"
            @empty={{not @model.booksReadByGenre}}
            @title={{t "reporting.chartTitles.dashboard.booksReadByGenre.rex"}}
            @tooltip={{t "reporting.tooltips.dashboard.booksReadByGenre"}}
          >
            {{#if @model.booksReadByGenre}}
              <DashboardBooksReadByGenre @data={{@model.booksReadByGenre}} />
            {{/if}}
          </ReportingDashboardStatic>
        </LinkTo>

        <LinkTo data-test-books-read-by-level @route="reporting.ui-scope.rex.books-read.by-level">
          <ReportingDashboardStatic
            class="h-full"
            @empty={{not @model.booksReadByLevel}}
            @title={{t "reporting.chartTitles.dashboard.booksReadByLevel"}}
            @tooltip={{t "reporting.tooltips.dashboard.booksReadByLevel"}}
          >
            {{#if @model.booksReadByLevel}}
              <DashboardBooksReadByLevel @data={{@model.booksReadByLevel}} />
            {{/if}}
          </ReportingDashboardStatic>
        </LinkTo>

        <LinkTo data-test-stadium-events @route="reporting.ui-scope.rex.stadium-events">
          <ReportingDashboardStatic
            class="h-full"
            @empty={{not @model.stadiumEvents}}
            @title={{t "reporting.chartTitles.dashboard.stadiumEvents"}}
            @tooltip={{t "reporting.tooltips.dashboard.stadiumEvents"}}
          >
            {{#if @model.stadiumEvents}}
              <DashboardStadiumEvents @data={{@model.stadiumEvents}} />
            {{/if}}
          </ReportingDashboardStatic>
        </LinkTo>

        <LinkTo data-test-awards-earned @route="reporting.ui-scope.rex.awards-earned">
          <ReportingDashboardStatic
            class="h-full"
            @empty={{not @model.awardsEarned}}
            @title={{t "reporting.chartTitles.dashboard.awardsEarned.rex"}}
            @tooltip={{t "reporting.tooltips.dashboard.awardsEarned.rex"}}
          >
            {{#if @model.awardsEarned}}
              <AwardsEarnedTrophies class="h-full" @data={{@model.awardsEarned}} />
            {{/if}}
          </ReportingDashboardStatic>
        </LinkTo>
      </div>
    </div>
  </template>
}

export default RouteTemplate(RexDashboardIndexPage)
